<template>
  <section class="py-12 bg-white sm:py-16">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Heading -->
      <p
        class="inline-block rounded-full bg-green-50 px-3 py-1 text-sm font-semibold uppercase tracking-wide text-wiq-green"
      >
        Growth ceiling calculator
      </p>
      <h1
        class="mt-3 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl"
      >
        What's your club's growth ceiling?
      </h1>
      <p class="mt-4 max-w-3xl text-lg text-gray-600">
        The “Max MRR” metric predicts revenue plateaus based on churn and new revenue. Move the
        sliders to see how a single point of retention reshapes everything.
      </p>

      <div class="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <!-- INPUTS -->
        <div class="bg-white rounded-xl shadow-lg p-8">
          <h2
            class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-900"
          >
            <span class="inline-block w-1.5 h-4 rounded-sm bg-wiq-green"></span
            >Inputs
          </h2>

          <div class="mt-6 space-y-7">
            <!-- Leads -->
            <div>
              <div class="flex justify-between items-baseline">
                <label for="cp-leads" class="text-sm font-medium text-gray-700"
                  >Leads per month</label
                >
                <span class="font-mono text-base font-bold text-wiq-green">{{
                  leads
                }}</span>
              </div>
              <input
                id="cp-leads"
                v-model.number="leads"
                type="range"
                min="5"
                max="200"
                step="1"
                class="wiq-range mt-2 w-full accent-wiq-green"
              />
            </div>

            <!-- Conversion -->
            <div>
              <div class="flex justify-between items-baseline">
                <label for="cp-conv" class="text-sm font-medium text-gray-700"
                  >Lead &rarr; member conversion</label
                >
                <span class="font-mono text-base font-bold text-wiq-green"
                  >{{ conversionPct }}%</span
                >
              </div>
              <input
                id="cp-conv"
                v-model.number="conversionPct"
                type="range"
                min="5"
                max="100"
                step="1"
                class="wiq-range mt-2 w-full accent-wiq-green"
              />
            </div>

            <!-- Churn + health band -->
            <div>
              <div class="flex justify-between items-baseline">
                <label for="cp-churn" class="text-sm font-medium text-gray-700">
                  Monthly churn
                  <span class="font-normal text-gray-400">(cancellations)</span>
                </label>
                <span class="font-mono text-base font-bold text-wiq-green"
                  >{{ churnPct.toFixed(1) }}%</span
                >
              </div>
              <input
                id="cp-churn"
                v-model.number="churnPct"
                type="range"
                min="0.5"
                max="20"
                step="0.1"
                class="wiq-range mt-2 w-full accent-wiq-green"
              />
              <div class="mt-2">
                <div
                  class="relative flex h-2.5 w-full rounded-full overflow-hidden"
                >
                  <span
                    class="h-full bg-green-400"
                    :style="{ width: churnBand.healthyWidth + '%' }"
                  ></span>
                  <span
                    class="h-full bg-yellow-400"
                    :style="{ width: churnBand.averageWidth + '%' }"
                  ></span>
                  <span
                    class="h-full bg-red-400"
                    :style="{ width: churnBand.dangerWidth + '%' }"
                  ></span>
                  <span
                    class="absolute top-0 h-full w-0.5 bg-gray-900"
                    :style="{ left: churnBand.markerLeft + '%' }"
                  ></span>
                </div>
                <div
                  class="mt-1 flex justify-between font-mono text-xxs uppercase tracking-wide"
                >
                  <span class="text-green-600">Healthy &lt;5.5%</span>
                  <span class="text-yellow-600">Average 5.5–12%</span>
                  <span class="text-red-600">Danger 12%+</span>
                </div>
              </div>
            </div>

            <!-- Price -->
            <div>
              <div class="flex justify-between items-baseline">
                <label for="cp-rev" class="text-sm font-medium text-gray-700"
                  >Avg revenue per member / mo</label
                >
                <span class="font-mono text-base font-bold text-wiq-green">{{
                  formatCurrency(avgRevenuePerMember)
                }}</span>
              </div>
              <input
                id="cp-rev"
                v-model.number="avgRevenuePerMember"
                type="range"
                min="50"
                max="500"
                step="5"
                class="wiq-range mt-2 w-full accent-wiq-green"
              />
            </div>
          </div>
        </div>

        <!-- YOUR CEILING -->
        <div class="bg-white rounded-xl shadow-lg p-8">
          <h2
            class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-900"
          >
            <span class="inline-block w-1.5 h-4 rounded-sm bg-wiq-green"></span
            >Your ceiling
          </h2>

          <!-- MRR + member count as co-headliners, stacked vertically -->
          <div class="mt-6 space-y-6">
            <div>
              <p
                class="font-mono text-xs uppercase tracking-wide text-gray-500"
              >
                Max subscribers
              </p>
              <p
                class="mt-1 text-5xl font-extrabold leading-none text-wiq-green tabular-nums"
              >
                {{ formatNumber(maxMem) }}
              </p>
            </div>
            <div>
              <p
                class="font-mono text-xs uppercase tracking-wide text-gray-500"
              >
                Max monthly revenue
              </p>
              <p
                class="mt-1 text-5xl font-extrabold leading-none text-gray-900 tabular-nums"
              >
                {{ formatCurrency(maxRev) }}
              </p>
            </div>
          </div>
          <p class="mt-3 text-sm text-gray-500">
            at steady state — where new sign-ups equal cancellations
          </p>

          <!-- Stats -->
          <dl class="mt-6 grid grid-cols-3 gap-3">
            <div class="bg-gray-50 rounded-lg p-3">
              <dt
                class="font-mono text-xxs uppercase tracking-wide text-gray-500"
              >
                Net new / mo
              </dt>
              <dd class="mt-1 text-lg font-bold text-gray-900 tabular-nums">
                {{ formatNumber(newPerMonth) }}
              </dd>
            </div>
            <div class="bg-gray-50 rounded-lg p-3">
              <dt
                class="font-mono text-xxs uppercase tracking-wide text-gray-500"
              >
                Months to 80%
              </dt>
              <dd class="mt-1 text-lg font-bold text-gray-900 tabular-nums">
                {{ months80 === null ? "—" : months80 }}
              </dd>
            </div>
            <div class="bg-gray-50 rounded-lg p-3">
              <dt
                class="font-mono text-xxs uppercase tracking-wide text-gray-500"
              >
                Avg lifetime
              </dt>
              <dd class="mt-1 text-lg font-bold text-gray-900 tabular-nums">
                {{ formatNumber(lifetime) }} mo
              </dd>
            </div>
          </dl>

          <!-- Churn-zone verdict: why you're stuck -->
          <!-- min-height keeps the card from growing/shrinking as the verdict
               copy wraps to a different line count while you drag a slider —
               that reflow is what made the mobile sliders jump. -->
          <div
            class="mt-6 min-h-[150px] rounded-xl border-l-4 p-5"
            :class="verdict.box"
          >
            <p
              class="font-mono text-xs font-bold uppercase tracking-wider"
              :class="verdict.title"
            >
              {{ verdict.headline }}
            </p>
            <p class="mt-2 text-gray-700">{{ verdict.body }}</p>
          </div>
        </div>
      </div>

      <!-- GROWTH TRAJECTORY -->
      <div class="mt-8 bg-white rounded-xl shadow-lg p-8">
        <div class="flex justify-between items-start flex-wrap gap-3">
          <h2
            class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-900"
          >
            <span class="inline-block w-1.5 h-4 rounded-sm bg-wiq-green"></span
            >Growth trajectory · 60 months
          </h2>
          <div class="flex items-center gap-4 font-mono text-xs text-gray-500">
            <span class="flex items-center gap-1.5">
              <span class="inline-block w-4 h-0.5 bg-wiq-green"></span>Active
              subscribers
            </span>
            <span class="flex items-center gap-1.5">
              <span
                class="inline-block w-4 border-t-2 border-dashed border-gray-400"
              ></span
              >Max ceiling
            </span>
          </div>
        </div>
        <p class="mt-1 text-sm text-gray-500">
          Starting from zero. The ceiling is where new sign-ups exactly equal
          cancellations.
        </p>

        <svg
          class="mt-6 w-full"
          :viewBox="`0 0 ${chart.w} ${chart.h}`"
          role="img"
          aria-label="Projected subscribers over 60 months approaching the ceiling"
        >
          <line
            v-for="g in chart.gridLines"
            :key="`g-${g.y}`"
            :x1="chart.padL"
            :y1="g.y"
            :x2="chart.w - chart.padR"
            :y2="g.y"
            stroke="#f3f4f6"
            stroke-width="1"
          />
          <text
            v-for="g in chart.gridLines"
            :key="`t-${g.y}`"
            :x="chart.padL - 8"
            :y="g.y + 4"
            text-anchor="end"
            class="fill-gray-400"
            font-size="11"
          >
            {{ g.label }}
          </text>

          <path :d="chart.areaPath" fill="#27aa83" fill-opacity="0.08" />
          <polyline
            :points="chart.linePoints"
            fill="none"
            stroke="#27aa83"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
          />

          <line
            :x1="chart.padL"
            :y1="chart.ceilingY"
            :x2="chart.w - chart.padR"
            :y2="chart.ceilingY"
            stroke="#9ca3af"
            stroke-width="1.5"
            stroke-dasharray="6 4"
          />
          <text
            :x="chart.w - chart.padR"
            :y="chart.ceilingY - 8"
            text-anchor="end"
            class="fill-gray-500"
            font-size="11"
          >
            Ceiling · {{ formatNumber(maxMem) }}
          </text>

          <!-- X axis: a marker every 12 months -->
          <g v-for="t in chart.xTicks" :key="`x-${t.label}`">
            <line
              :x1="t.x"
              :y1="chart.baselineY"
              :x2="t.x"
              :y2="chart.baselineY + 4"
              stroke="#d1d5db"
              stroke-width="1"
            />
            <text
              :x="t.x"
              :y="chart.h - 6"
              text-anchor="middle"
              class="fill-gray-400"
              font-size="11"
            >
              {{ t.label }}
            </text>
          </g>
        </svg>
      </div>

      <p class="mt-8 text-sm text-gray-400">
        Based on Jason Cohen's
        <a
          href="https://longform.asmartbear.com/max-mrr/"
          target="_blank"
          rel="noopener noreferrer"
          class="underline hover:text-wiq-green"
          >&ldquo;Max MRR&rdquo;</a
        >
        concept, applied to wrestling club subscriptions.
      </p>
    </div>
  </section>
</template>

<script>
import {
  newMembersPerMonth,
  maxMembers,
  maxRevenue,
  avgMemberLifetime,
  simulateMembers,
  monthsTo80Percent,
  churnZone,
  HEALTHY_TARGET_CHURN,
} from './lib/max_members_calc';

const SIM_MONTHS = 60;
// Churn slider bounds — the health band and marker are positioned against these.
// Max runs to 20% because real wrestling-club churn reaches into the teens.
const CHURN_MIN = 0.5;
const CHURN_MAX = 20;

export default {
  name: 'max-mrr-control-panel',
  data() {
    return {
      leads: 40,
      conversionPct: 50,
      churnPct: 8,
      avgRevenuePerMember: 180,
      // The "results" cards (ceiling numbers, verdict, chart) read from this
      // snapshot instead of the live slider values. We refresh it on an
      // animation frame rather than on every `input` event, so dragging a
      // slider does not re-render (and reflow) the rest of the page on every
      // touchmove. iOS Safari jumps the range thumb when the DOM is mutated
      // mid-drag, so we keep those mutations off the synchronous touch handler.
      committed: {
        leads: 40,
        conversionPct: 50,
        churnPct: 8,
        avgRevenuePerMember: 180,
      },
      rafId: null,
    };
  },
  watch: {
    leads: 'scheduleCommit',
    conversionPct: 'scheduleCommit',
    churnPct: 'scheduleCommit',
    avgRevenuePerMember: 'scheduleCommit',
  },
  beforeDestroy() {
    if (this.rafId !== null) window.cancelAnimationFrame(this.rafId);
  },
  computed: {
    conversionRate() {
      return this.committed.conversionPct / 100;
    },
    churnRate() {
      return this.committed.churnPct / 100;
    },
    newPerMonth() {
      return newMembersPerMonth(this.committed.leads, this.conversionRate);
    },
    maxMem() {
      return maxMembers(this.committed.leads, this.conversionRate, this.churnRate);
    },
    maxRev() {
      return maxRevenue(
        this.committed.leads,
        this.conversionRate,
        this.churnRate,
        this.committed.avgRevenuePerMember,
      );
    },
    lifetime() {
      return avgMemberLifetime(this.churnRate);
    },
    months80() {
      return monthsTo80Percent(this.churnRate, SIM_MONTHS);
    },
    series() {
      return simulateMembers(
        this.committed.leads,
        this.conversionRate,
        this.churnRate,
        SIM_MONTHS,
      );
    },
    // Geometry for the green/yellow/red churn health band under the slider.
    // Segment widths and the current-value marker are positioned against the
    // churn slider's own range so the marker lines up with the thumb.
    churnBand() {
      const span = CHURN_MAX - CHURN_MIN;
      const pctOf = (value) => ((value - CHURN_MIN) / span) * 100;
      return {
        healthyWidth: pctOf(5.5), // < 5.5% healthy (top quartile)
        averageWidth: pctOf(12) - pctOf(5.5), // 5.5–12% average
        dangerWidth: 100 - pctOf(12), // 12%+ danger (bottom quartile)
        markerLeft: Math.min(Math.max(pctOf(this.churnPct), 0), 100),
      };
    },
    // The ceiling you'd reach if churn dropped to the healthy 3% benchmark.
    healthyCeiling() {
      return this.newPerMonth / HEALTHY_TARGET_CHURN;
    },
    // The churn-zone verdict — names the zone and shows what reaching the
    // healthy 3% benchmark would do to the ceiling. The "why you're stuck" payoff.
    verdict() {
      const zone = churnZone(this.churnRate);
      const headline = {
        healthy: 'Churn looks good',
        average: 'Average zone',
        danger: 'Danger zone',
      }[zone];

      let body;
      if (zone === 'healthy') {
        // Already top-quartile — don't nag about churn; point them at the funnel.
        body = "Your churn is top-quartile for a wrestling club, so retention isn't what's holding you "
          + 'back. Play with the leads and conversion sliders to push your ceiling higher.';
      } else {
        const lead = {
          average: 'About average for a wrestling club — real room to improve.',
          danger:
            'High churn even for wrestling — retention is capping growth.',
        }[zone];
        body = `${lead} Dropping churn from ${this.committed.churnPct.toFixed(
          1,
        )}% to 5.5% (the best clubs' retention) `
          + `would lift your ceiling from ${this.formatNumber(this.maxMem)} to `
          + `${this.formatNumber(
            this.healthyCeiling,
          )} subscribers — same lead flow.`;
      }

      const styles = {
        healthy: {
          box: 'bg-green-50 border-wiq-green',
          title: 'text-wiq-green',
        },
        average: {
          box: 'bg-yellow-50 border-yellow-400',
          title: 'text-yellow-700',
        },
        danger: { box: 'bg-red-50 border-red-500', title: 'text-red-600' },
      }[zone];

      return {
        zone,
        headline,
        body,
        ...styles,
      };
    },
    chart() {
      const w = 720;
      const h = 300;
      const padL = 48;
      const padR = 16;
      const padT = 16;
      const padB = 28;

      const innerW = w - padL - padR;
      const innerH = h - padT - padB;

      // Give the ceiling a little headroom at the top of the plot.
      const yMax = this.maxMem * 1.12 || 1;

      const xFor = (i) => padL + (i / SIM_MONTHS) * innerW;
      const yFor = (v) => padT + innerH - (v / yMax) * innerH;

      const points = this.series.map(
        (v, i) => `${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`,
      );
      const linePoints = points.join(' ');

      const lastX = xFor(SIM_MONTHS);
      const baseY = padT + innerH;
      const areaPath = `M ${xFor(0).toFixed(1)},${baseY.toFixed(
        1,
      )} L ${points.join(' L ')} L ${lastX.toFixed(1)},${baseY.toFixed(1)} Z`;

      const ceilingY = yFor(this.maxMem);

      // Five evenly-spaced gridlines from 0 to the ceiling.
      const gridLines = [0, 0.25, 0.5, 0.75, 1].map((frac) => {
        const value = this.maxMem * frac;
        return { y: yFor(value), label: this.formatNumber(value) };
      });

      // X-axis markers every 12 months.
      const xTicks = [0, 12, 24, 36, 48, 60].map((m) => ({
        x: xFor(m),
        label: `${m}`,
      }));

      return {
        w,
        h,
        padL,
        padR,
        linePoints,
        areaPath,
        ceilingY,
        gridLines,
        xTicks,
        baselineY: baseY,
      };
    },
  },
  methods: {
    // Defer refreshing the results snapshot to the next animation frame, and
    // coalesce a burst of `input` events (one per touchmove) into a single
    // re-render. This keeps DOM mutation off the synchronous drag handler so
    // iOS Safari doesn't jump the slider thumb while you drag.
    scheduleCommit() {
      if (this.rafId !== null) return;
      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.committed = {
          leads: this.leads,
          conversionPct: this.conversionPct,
          churnPct: this.churnPct,
          avgRevenuePerMember: this.avgRevenuePerMember,
        };
      });
    },
    formatNumber(value) {
      if (!Number.isFinite(value)) return '∞';
      return Math.round(value).toLocaleString();
    },
    formatCurrency(value) {
      if (!Number.isFinite(value)) return '∞';
      return `$${Math.round(value).toLocaleString()}`;
    },
  },
};
</script>
